import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Activity, Flame, Clock, Users } from 'lucide-react';

function getSaludo(nombre) {
  const h = new Date().getHours();
  const firstName = (nombre || 'lobo').split(' ')[0];
  if (h >= 5 && h < 12) {
    return { texto: `Buenos días, ${firstName}`, emoji: '🌅', sub: 'Hoy es un buen día para sumar' };
  }
  if (h >= 12 && h < 17) {
    return { texto: `Buenas tardes, ${firstName}`, emoji: '☀️', sub: 'A mitad del día y el Klan no para' };
  }
  if (h >= 17 && h < 22) {
    return { texto: `¡A entrenar, ${firstName}!`, emoji: '🔥', sub: 'La hora de oro del lobo' };
  }
  return { texto: `Buenas noches, ${firstName}`, emoji: '🌙', sub: 'Descansa, mañana toca seguir' };
}

export default function KlanPulse() {
  const { user } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [roster, setRoster] = useState(null);
  const saludo = getSaludo(user?.nombre);

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/community/pulse`),
        axios.get(`${API}/community/today-roster`),
      ]);
      setPulse(p.data);
      setRoster(r.data);
    } catch (e) {
      // silencioso
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // refresca cada 1 min
    return () => clearInterval(interval);
  }, [load]);

  const nextTxt = (() => {
    if (!pulse?.next_training) return null;
    const m = pulse.next_training.en_minutos;
    if (m < 0) return null;
    if (m < 60) return `Próximo entreno en ${m} min`;
    const h = Math.round(m / 60);
    return `Próximo entreno en ${h}h`;
  })();

  return (
    <div className="mb-4 space-y-3" data-testid="klan-pulse">
      {/* Saludo personalizado */}
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl">{saludo.emoji}</span>
        <h2 className="text-xl sm:text-2xl font-bold text-white">{saludo.texto}</h2>
        <span className="text-gray-500 text-sm italic">— {saludo.sub}</span>
      </div>

      {/* Pulso del Klan (barra) */}
      {pulse && (pulse.active_today > 0 || pulse.entrenos_hoy_total > 0) && (
        <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm bg-gradient-to-r from-gray-900/80 to-gray-900/40 border border-gold/20 rounded-full px-4 py-2">
          <span className="flex items-center gap-1.5 text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <b>{pulse.active_today}</b> <span className="text-gray-300">activos hoy</span>
          </span>
          {pulse.entrenos_en_marcha > 0 && (
            <span className="flex items-center gap-1 text-orange-400">
              <Flame className="h-3 w-3" /> <b>{pulse.entrenos_en_marcha}</b> en marcha
            </span>
          )}
          {pulse.entrenos_hoy_total > 0 && (
            <span className="flex items-center gap-1 text-gray-300">
              <Activity className="h-3 w-3 text-gold" /> <b>{pulse.entrenos_hoy_total}</b> entrenos hoy
            </span>
          )}
          {nextTxt && (
            <span className="flex items-center gap-1 text-gold">
              <Clock className="h-3 w-3" /> {nextTxt}
            </span>
          )}
        </div>
      )}

      {/* Roster de hoy */}
      {roster && roster.total > 0 && (
        <div className="flex items-center gap-2" data-testid="today-roster">
          <Users className="h-4 w-4 text-gold flex-shrink-0" />
          <span className="text-xs text-gray-400 flex-shrink-0">Hoy entrenan:</span>
          <div className="flex items-center -space-x-2 flex-1 overflow-hidden">
            {roster.users.slice(0, 8).map((u, i) => (
              <div
                key={u.id}
                className="w-7 h-7 rounded-full border-2 border-gray-900 overflow-hidden bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-[10px] text-gray-300 font-bold flex-shrink-0"
                title={u.nombre}
                style={{ zIndex: 10 - i }}
              >
                {u.foto_perfil ? (
                  <img
                    src={u.foto_perfil.startsWith('http') ? u.foto_perfil : `${BACKEND_URL}${u.foto_perfil}`}
                    alt={u.nombre}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (u.nombre || 'U')[0].toUpperCase()
                )}
              </div>
            ))}
            {roster.total > 8 && (
              <div className="w-7 h-7 rounded-full border-2 border-gray-900 bg-gold text-gray-900 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                +{roster.total - 8}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
